import { RadioClient } from '../transport/radioClient';
import { LocationTracker } from '../geo/locationTracker';
import { SessionManager } from '../storage/sessionManager';
import { getDb, StoredTrace } from '../storage/db';
import {
  createTraceroutePacket,
  decodeRouteDiscovery,
  formatNodeHex,
  DecodedMeshPacket
} from '../protocol/codec';

export interface TracerouteStats {
  sentCount: number;
  ackCount: number;
  successRatePercent: number;
  lastRttMs: number | null;
  lastRoute: number[];
}

export type TraceResolvedHandler = (trace: StoredTrace) => void;

/**
 * Auto Traceroute engine for sending automated, periodic TRACEROUTE_APP requests
 * and tracking multi-hop routes, RTT, and ACK success rates with GPS georeferencing.
 */
export class TracerouteEngine {
  private isRunning = false;
  private intervalTimer: ReturnType<typeof setInterval> | null = null;
  private targetNode = 0xffffffff; // Default to broadcast
  private intervalSeconds = 30; // Duty-cycle safe default

  private sentTraces: Map<number, StoredTrace> = new Map();
  private stats: TracerouteStats = {
    sentCount: 0,
    ackCount: 0,
    successRatePercent: 0,
    lastRttMs: null,
    lastRoute: []
  };

  private traceResolvedHandlers: TraceResolvedHandler[] = [];
  private statsChangeHandlers: ((stats: TracerouteStats) => void)[] = [];

  constructor(
    private radioClient: RadioClient,
    private locationTracker: LocationTracker,
    private sessionManager: SessionManager
  ) {
    this.handleIncomingPacket = this.handleIncomingPacket.bind(this);
    this.radioClient.on('packet', this.handleIncomingPacket);
  }

  /**
   * Starts periodic traceroute transmissions at the configured interval.
   *
   * @param targetNode - Destination 32-bit node ID (or 0xFFFFFFFF for broadcast)
   * @param intervalSeconds - Interval between pings (minimum 10s for duty cycle safety)
   */
  start(targetNode = 0xffffffff, intervalSeconds = 30): void {
    this.stop();
    this.targetNode = targetNode >>> 0;
    // Enforce minimum 10s interval to prevent channel congestion and respect LoRa duty cycles
    this.intervalSeconds = Math.max(10, intervalSeconds);
    this.isRunning = true;

    // Send immediate first traceroute
    this.sendSingleTraceroute().catch((err) => {
      console.warn('[TracerouteEngine] Immediate ping failed:', err);
    });

    this.intervalTimer = setInterval(() => {
      if (this.isRunning) {
        this.sendSingleTraceroute().catch((err) => {
          console.warn('[TracerouteEngine] Periodic ping failed:', err);
        });
      }
    }, this.intervalSeconds * 1000);
  }

  /**
   * Stops periodic traceroute transmissions.
   */
  stop(): void {
    this.isRunning = false;
    if (this.intervalTimer !== null) {
      clearInterval(this.intervalTimer);
      this.intervalTimer = null;
    }
  }

  /**
   * Dynamically updates transmission interval while keeping active loop synchronized.
   *
   * Why: Allows operators to adjust duty-cycle intervals in runtime settings without interrupting active tracking.
   *
   * @param intervalSeconds - Interval between cycles in seconds (enforces min 10s)
   */
  setInterval(intervalSeconds: number): void {
    this.intervalSeconds = Math.max(10, intervalSeconds);
    if (this.isRunning && this.intervalTimer !== null) {
      clearInterval(this.intervalTimer);
      this.intervalTimer = setInterval(() => {
        if (this.isRunning) {
          this.sendSingleTraceroute().catch((err) => {
            console.warn('[TracerouteEngine] Periodic ping failed:', err);
          });
        }
      }, this.intervalSeconds * 1000);
    }
  }

  /**
   * Returns destination node ID currently targeted by the engine.
   *
   * Why: Enables UI components to determine whether an active traceroute matches a selected node marker.
   *
   * @returns 32-bit destination station ID
   */
  getTargetNode(): number {
    return this.targetNode;
  }

  /**
   * Returns the current interval in seconds.
   *
   * Why: Synchronizes UI indicators and badges with actual duty cycle timing.
   *
   * @returns Current interval period in seconds
   */
  getInterval(): number {
    return this.intervalSeconds;
  }

  /**
   * Dispatches an individual traceroute packet immediately.
   *
   * Why: Enables ad-hoc single-hop/multi-hop path probing on demand without starting continuous background polling.
   *
   * @param targetNode - Optional destination node override; defaults to active target
   * @returns Generated 32-bit packet ID
   */
  async sendSingleTraceroute(targetNode?: number): Promise<number> {
    const activeSession = this.sessionManager.getActiveSession();
    if (!activeSession) {
      throw new Error('No active session.');
    }

    const myNodeInfo = this.radioClient.getMyNodeInfo();
    const myNodeNum = myNodeInfo ? myNodeInfo.myNodeNum : 0;
    const dest = (targetNode !== undefined ? targetNode : this.targetNode) >>> 0;

    // Generate random 32-bit packet identifier
    const packetId = Math.floor(Math.random() * 0x7fffffff) >>> 0;
    const packetBytes = createTraceroutePacket(dest, myNodeNum, packetId);

    const geo = this.locationTracker.getLatestSnapshot();
    const now = Date.now();

    const traceRecord: StoredTrace = {
      packetId,
      sessionId: activeSession.id,
      sentAt: now,
      targetNode: dest,
      targetHex: formatNodeHex(dest),
      lat: geo ? geo.lat : null,
      lon: geo ? geo.lon : null,
      ackReceived: false,
      ackAt: null,
      rttMs: null,
      pathNodes: [],
      pathSnrs: []
    };

    this.sentTraces.set(packetId, traceRecord);
    this.stats.sentCount++;
    this.updateStats();

    // Persist sent trace attempt into IndexedDB
    const db = await getDb();
    await db.put('traces', traceRecord);

    // Transmit over radio
    await this.radioClient.send(packetBytes);

    return packetId;
  }

  /**
   * Evaluates incoming mesh packets to match traceroute responses and compute RTT.
   *
   * @param packet - Decoded MeshPacket from radio
   */
  private async handleIncomingPacket(packet: DecodedMeshPacket): Promise<void> {
    const reqId = packet.decoded?.requestId;
    const replyId = packet.decoded?.replyId;
    const isTraceroutePort = packet.decoded?.portnum === 70; // TRACEROUTE_APP

    // Check if packet correlates with an active pending trace
    let matchedTrace: StoredTrace | undefined;
    if (reqId && this.sentTraces.has(reqId >>> 0)) {
      matchedTrace = this.sentTraces.get(reqId >>> 0);
    } else if (replyId && this.sentTraces.has(replyId >>> 0)) {
      matchedTrace = this.sentTraces.get(replyId >>> 0);
    } else if (isTraceroutePort) {
      // Find latest pending trace matching target
      for (const t of this.sentTraces.values()) {
        if (!t.ackReceived && (t.targetNode === packet.from || t.targetNode === 0xffffffff)) {
          matchedTrace = t;
          break;
        }
      }
    }

    if (matchedTrace && !matchedTrace.ackReceived) {
      const now = Date.now();
      matchedTrace.ackReceived = true;
      matchedTrace.ackAt = now;
      matchedTrace.rttMs = now - matchedTrace.sentAt;

      // Parse RouteDiscovery if carried in payload
      if (isTraceroutePort && packet.decoded?.payload) {
        try {
          const routeDiscovery = decodeRouteDiscovery(packet.decoded.payload);
          if (routeDiscovery.route) {
            matchedTrace.pathNodes = routeDiscovery.route.map((n) => n >>> 0);
          }
          if (routeDiscovery.snrTowards) {
            matchedTrace.pathSnrs = routeDiscovery.snrTowards;
          }
        } catch (err) {
          console.warn('[TracerouteEngine] Could not decode RouteDiscovery payload:', err);
        }
      }

      this.stats.ackCount++;
      this.stats.lastRttMs = matchedTrace.rttMs;
      this.stats.lastRoute = matchedTrace.pathNodes;
      this.updateStats();

      // Update in IndexedDB
      const db = await getDb();
      await db.put('traces', matchedTrace);

      // Notify subscribers
      for (const h of this.traceResolvedHandlers) {
        try {
          h(matchedTrace);
        } catch (err) {
          console.error('[TracerouteEngine] Trace resolved handler error:', err);
        }
      }
    }
  }

  private updateStats(): void {
    if (this.stats.sentCount > 0) {
      this.stats.successRatePercent = Number(
        ((this.stats.ackCount / this.stats.sentCount) * 100).toFixed(1)
      );
    } else {
      this.stats.successRatePercent = 0;
    }

    for (const h of this.statsChangeHandlers) {
      try {
        h({ ...this.stats });
      } catch (err) {
        console.error('[TracerouteEngine] Stats change handler error:', err);
      }
    }
  }

  getStats(): TracerouteStats {
    return { ...this.stats };
  }

  isActive(): boolean {
    return this.isRunning;
  }

  onTraceResolved(handler: TraceResolvedHandler): void {
    this.traceResolvedHandlers.push(handler);
  }

  onStatsChange(handler: (stats: TracerouteStats) => void): void {
    this.statsChangeHandlers.push(handler);
  }
}
